
import React, { useEffect, useState, useRef } from 'react';
import { usePomodoro } from '../../context/PomodoroContext';
import { pomodoroStateStorage, isLocalStorageAvailable } from '../../utils/storage/localStorage';

export default function PomodoroTimer() {
  const { state } = usePomodoro();
  const [isResumed, setIsResumed] = useState(false);
  const [originalTitle, setOriginalTitle] = useState('');
  const prevStateRef = useRef(state);
  
  // 检测是否是从刷新页面恢复的状态
  useEffect(() => {
    // 这段代码只会在客户端执行
    if (isLocalStorageAvailable()) {
      const parsedState = pomodoroStateStorage.get();
      if (parsedState && (parsedState.isActive || parsedState.isPaused)) {
        setIsResumed(true);
        // 5秒后隐藏恢复提示
        const timer = setTimeout(() => {
          setIsResumed(false);
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, []);
  
  // 保存原始标题并更新浏览器标题
  useEffect(() => {
    // 保存原始标题（只在组件首次加载时）
    if (!originalTitle) {
      setOriginalTitle(document.title);
    }
    
    // 检测休息结束的情况
    const prevState = prevStateRef.current;
    const isBreakCompleted = 
      prevState.mode === 'break' && 
      prevState.isActive && 
      !state.isActive && 
      state.mode === 'work';
    
    // 更新浏览器标题
    if (state.isActive) {
      const formattedTime = formatTime(state.timeLeft);
      const modeText = state.mode === 'work' ? '工作中' : '休息中';
      document.title = `(${formattedTime}) ${modeText} - Pomodoro`;
    } else if (isBreakCompleted || (state.mode === 'work' && prevState.mode === 'break')) {
      // 休息结束时，强制恢复原始标题
      document.title = originalTitle || 'Pomodoro Tracker';
    } else if (!state.isActive && !state.isPaused) {
      // 如果计时器不活跃且不是暂停状态，恢复原始标题
      if (originalTitle) {
        document.title = originalTitle;
      }
    }
    
    // 更新前一个状态的引用
    prevStateRef.current = state;
    
    // 组件卸载时恢复原始标题
    return () => {
      if (originalTitle) {
        document.title = originalTitle;
      }
    };
  }, [state, originalTitle]);
  
  // 获取恢复提示信息
  const getResumeMessage = () => {
    if (state.mode === 'work') {
      return '工作计时已暂停，请点击"继续"按钮继续计时，或点击"已完成"按钮结束当前工作';
    } else {
      return '休息计时已暂停，请点击"继续"按钮继续计时，或点击"跳过"按钮跳过休息';
    }
  };

  // 将秒数转换为分:秒格式
  const formatTime = (timeInSeconds: number) => {
    // 处理无效值
    if (isNaN(timeInSeconds) || timeInSeconds === undefined || timeInSeconds === null) {
      console.warn('Invalid timeInSeconds value:', timeInSeconds);
      return '00:00';
    }
    
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col items-center justify-center">
      {state.isActive ? (
        <div className="text-6xl font-bold">
          {formatTime(state.timeLeft)}
        </div>
      ) : (
        <div>
          <div className="text-6xl font-bold">
            {formatTime(state.timeLeft)}
          </div>
          
          {isResumed && (
            <div className="text-xs text-white opacity-80 mt-2 animate-pulse">
              {getResumeMessage()}
            </div>
          )}
        </div>
      )}
    </div>
  );
} 